import { Component, type ErrorInfo, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { Button } from "@celink/ui"
import { AlertTriangle, Home, RefreshCw } from "lucide-react"

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary:", error, errorInfo)
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback
      const message = this.state.error.message || "Algo salió mal"
      const is404 = /404|not found/i.test(message)
      return (
        <div
          style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 40 }}
        >
          <AlertTriangle size={64} style={{ color: "var(--primary-color)" }} aria-hidden />
          <h2 style={{ margin: 0 }}>
            {is404 ? "Página no encontrada" : "Algo salió mal"}
          </h2>
          <p style={{ maxWidth: 400, textAlign: "center", color: "#6b7280", marginBottom: 0 }}>
            {is404
              ? "La ruta a la que intentas acceder no existe."
              : "Ocurrió un error inesperado. Puedes volver al inicio o recargar."}
          </p>
          <pre style={{ maxWidth: 700, maxHeight: 200, overflow: "auto", fontSize: 11, background: "#fef2f2", padding: 12, borderRadius: 8, border: "1px solid #fecaca", color: "#991b1b", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {this.state.error.message}{"\n\n"}{this.state.error.stack}
          </pre>
          <div style={{ display: "flex", gap: 8 }}>
            <Link to="/">
              <Button><Home size={16} style={{ marginRight: 6 }} />Ir al inicio</Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
            >
              <RefreshCw size={16} style={{ marginRight: 6 }} />Recargar
            </Button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
